import { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, ClipboardList, Calculator, BookOpen,
  CalendarDays, ShoppingCart, ArrowLeftRight, Banknote, FileArchive,
  Moon, Sun, Search, Bell, HardHat, ChevronRight, Users, BarChart3, FileSpreadsheet, Link2, Scissors, LayoutGrid, FileSignature
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getCompany } from "@/lib/api";
import { CompanyLogo, CompanySwitcher, useCompanies } from "@/components/CompanySwitcher";

const NAV = [
  { to: "/", label: "Genel Bakış", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/projeler", label: "Projeler ve Şantiyeler", icon: Building2, testid: "nav-projects" },
  { to: "/firmalar", label: "Firmalar", icon: Users, testid: "nav-firms" },
  { to: "/is-kalemleri", label: "İş Kalemleri", icon: ClipboardList, testid: "nav-workitems" },
  { to: "/kesif", label: "Keşif", icon: Calculator, testid: "nav-discovery" },
  { to: "/santiye-gunlugu", label: "Şantiye Günlüğü", icon: BookOpen, testid: "nav-sitelogs" },
  { to: "/puantaj", label: "Puantaj", icon: CalendarDays, testid: "nav-timesheets" },
  { to: "/malzeme-talepleri", label: "Malzeme Talepleri", icon: ShoppingCart, testid: "nav-material-requests" },
  { to: "/malzeme-hareketleri", label: "Malzeme Hareketleri", icon: ArrowLeftRight, testid: "nav-material-movements" },
  { to: "/hakedis", label: "Hakediş", icon: Banknote, testid: "nav-payments", hide: "tkc" },
  { to: "/sozlesmeler", label: "Hakediş", icon: FileSignature, testid: "nav-contracts", only: "tkc" },
  { to: "/kesintiler", label: "Kesintiler", icon: Scissors, testid: "nav-deductions" },
  { to: "/belgeler", label: "Çizim ve Belgeler", icon: FileArchive, testid: "nav-documents" },
  { to: "/analiz", label: "Bütçe ve Analiz", icon: BarChart3, testid: "nav-analytics" },
  { to: "/kalem-esleme", label: "Kalem Eşleme", icon: Link2, testid: "nav-item-matching" },
  { to: "/veri-aktarimi", label: "Veri Aktarımı", icon: FileSpreadsheet, testid: "nav-import" },
];

export default function Layout() {
  const [dark, setDark] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const toLanding = () => { sessionStorage.removeItem("entered"); navigate("/giris"); };
  const { list: companies, active } = useCompanies();

  useEffect(() => {
    const saved = localStorage.getItem("theme") === "dark";
    setDark(saved);
    document.documentElement.classList.toggle("dark", saved);
  }, []);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  const activeNav = NAV.find(n => n.to === location.pathname) || NAV.find(n => n.to !== "/" && location.pathname.startsWith(n.to)) || NAV[0];

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <aside className="w-64 xl:w-72 shrink-0 border-r border-border bg-slate-950 text-slate-100 min-h-screen sticky top-0 h-screen overflow-y-auto">
        <div className="h-16 flex items-center gap-3 px-5 border-b border-slate-800">
          {active?.logo_url ? <CompanyLogo company={active} className="w-10 h-10 rounded-lg" testid="sidebar-company-logo" /> : (
            <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center">
              <HardHat className="w-5 h-5 text-slate-950" />
            </div>
          )}
          <div className="font-display font-bold text-base leading-tight">
            Şantiye<br />
            <span className="text-amber-400 text-xs font-semibold tracking-wider" data-testid="sidebar-company-name">{active?.name || "YÖNETİM"}</span>
          </div>
        </div>
        <nav className="p-3 space-y-1" data-testid="sidebar-nav">
          {NAV.filter(n => (!n.only || n.only === getCompany()) && n.hide !== getCompany()).map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all",
                isActive
                  ? "bg-amber-500/15 text-amber-300 border-l-2 border-amber-500 font-semibold"
                  : "text-slate-300 hover:bg-slate-800/70 hover:text-white border-l-2 border-transparent"
              )}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 mt-6">
          <div className="rounded-lg bg-slate-900 border border-slate-800 p-3 text-xs text-slate-400">
            <div className="text-amber-400 font-semibold mb-1">Demo Mod</div>
            Tüm veriler lokal demo içindir.
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-40 h-16 border-b border-border bg-background/95 backdrop-blur flex items-center px-6 gap-4">
          <div className="flex items-center text-sm text-muted-foreground">
            <span>Şantiye</span>
            <ChevronRight className="w-4 h-4 mx-1.5" />
            <span className="text-foreground font-semibold" data-testid="active-page-title">{activeNav.label}</span>
          </div>
          <div className="flex-1" />
          <CompanySwitcher list={companies} />
          <button onClick={toLanding} className="h-9 px-3 rounded-lg border border-border hover:border-amber-500 hover:text-amber-500 transition-colors flex items-center gap-1.5 text-sm" data-testid="back-to-landing-button" title="Açılış sayfası">
            <LayoutGrid className="w-4 h-4" /> <span className="hidden xl:inline">Açılış</span>
          </button>
          <div className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-muted-foreground w-72">
            <Search className="w-4 h-4" />
            <input
              className="bg-transparent outline-none flex-1 text-foreground placeholder:text-muted-foreground"
              placeholder="Ara (poz, şantiye, personel)"
              data-testid="topbar-search"
            />
            <kbd className="hidden lg:inline text-[10px] px-1.5 py-0.5 rounded bg-background border border-border">Ctrl+K</kbd>
          </div>
          <button
            onClick={toggleTheme}
            className="w-9 h-9 rounded-lg border border-border hover:border-amber-500 hover:text-amber-500 transition-colors flex items-center justify-center"
            data-testid="theme-toggle"
            aria-label="Tema"
          >
            {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button className="relative w-9 h-9 rounded-lg border border-border hover:border-amber-500 flex items-center justify-center" data-testid="notification-button">
            <Bell className="w-4 h-4" />
          </button>
          <div className="w-9 h-9 rounded-lg bg-slate-900 text-white font-bold flex items-center justify-center text-sm" data-testid="user-avatar">{active?.short?.[0] || "S"}</div>
        </header>

        {/* Content */}
        <main className="flex-1 p-6 lg:p-8 animate-in">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
